import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { RiskBadge } from "@/components/skills/RiskBadge";
import { Stars } from "@/components/skills/Stars";
import { SCENARIOS } from "@/lib/skills/constants";
import { recommendationsFor } from "@/lib/skills/recommend";
import type { SkillListItem } from "@/lib/skills/types";
import { cn } from "@/lib/utils";

export function Recommendations({ skills }: { skills: SkillListItem[] }) {
  const [scenarioId, setScenarioId] = useState(SCENARIOS[0]?.id ?? "new-build");
  const recs = recommendationsFor(scenarioId, skills);
  return (
    <section className="rounded-lg border border-border-1 bg-white p-5">
      <div className="flex items-baseline justify-between">
        <h2 className="font-display text-lg font-bold text-fg-1">Recommended for your scenario</h2>
        <span className="text-xs text-fg-3">Rule-based · no AI guesswork</span>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        {SCENARIOS.map((s) => {
          const Icon = s.icon;
          const active = s.id === scenarioId;
          return (
            <button
              key={s.id}
              type="button"
              onClick={() => setScenarioId(s.id)}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-medium transition-colors",
                active
                  ? "border-blue bg-bg-accent text-blue"
                  : "border-border-1 text-fg-2 hover:bg-bg-2",
              )}
            >
              <Icon className="size-4" />
              {s.label}
            </button>
          );
        })}
      </div>
      <div className="mt-4 grid gap-3 md:grid-cols-3">
        {recs.map(({ skill, reason }) => (
          <Link
            key={skill.id}
            to="/skill/$id"
            params={{ id: skill.slug }}
            className="group flex flex-col gap-2 rounded-lg border border-border-1 bg-bg-2 p-4 transition-all hover:-translate-y-[3px] hover:shadow-md"
          >
            <span className="w-fit rounded-full bg-bg-accent px-2 py-0.5 text-[11px] font-semibold text-blue">
              {reason}
            </span>
            <h3 className="font-display text-sm font-bold text-fg-1 group-hover:text-blue">
              {skill.title}
            </h3>
            <p className="line-clamp-2 text-xs text-fg-2">{skill.desc}</p>
            <div className="mt-auto flex items-center justify-between pt-1">
              <Stars value={skill.rating} count={skill.ratingCount} />
              <RiskBadge level={skill.risk} />
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}
